import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { apiGet } from '../utils/api';

interface Organization {
  id: number;
  name: string;
  slug: string;
}

interface Institution {
  id: number;
  name: string;
  slug: string;
  organization_id: number;
}

interface OrgInstitutionContextType {
  selectedOrganizationId: number | null;
  selectedInstitutionId: number | null;
  organizations: Organization[];
  institutions: Institution[];
  setSelectedOrganizationId: (orgId: number | null) => void;
  setSelectedInstitutionId: (instId: number | null) => void;
  isLoading: boolean;
  refreshInstitutions: () => void;
}

const OrgInstitutionContext = createContext<OrgInstitutionContextType | null>(null);

export const useOrgInstitution = (): OrgInstitutionContextType => {
  const context = useContext(OrgInstitutionContext);
  if (!context) {
    throw new Error('useOrgInstitution must be used within an OrgInstitutionProvider');
  }
  return context;
};

interface OrgInstitutionProviderProps {
  children: ReactNode;
}

export const OrgInstitutionProvider: React.FC<OrgInstitutionProviderProps> = ({ children }) => {
  const { user, token, isAuthenticated } = useAuth();
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<number | null>(null);
  const [selectedInstitutionId, setSelectedInstitutionId] = useState<number | null>(null);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [institutions, setInstitutions] = useState<Institution[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch organizations (only for global admins)
  const fetchOrganizations = async () => {
    if (!token || !isAuthenticated || user?.user_role !== 'global_admin') return;
    
    try {
      setIsLoading(true);
      const response = await apiGet('/api/organizations?status=active&per_page=1000', token);
      
      if (response.ok) {
        const data = await response.json();
        setOrganizations(data.organizations || []);
      }
    } catch (err) {
      console.error('Error fetching organizations:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch institutions based on selected organization or user context
  const fetchInstitutions = async () => {
    if (!token || !isAuthenticated) return;
    
    try {
      setIsLoading(true);
      let url = '/api/institutions?status=active&per_page=1000';
      
      // If super admin with org selected, filter by org
      if (user?.user_role === 'global_admin' && selectedOrganizationId) {
        url += `&organization_id=${selectedOrganizationId}`;
      }
      
      const response = await apiGet(url, token);
      
      if (response.ok) {
        const data = await response.json();
        setInstitutions(data.institutions || []);
      }
    } catch (err) {
      console.error('Error fetching institutions:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Load data on mount or when user/org changes
  useEffect(() => {
    if (!isAuthenticated || !user) return;
    
    // Load organizations for global admins
    if (user.user_role === 'global_admin') {
      fetchOrganizations();
    }
    
    // Load institutions
    fetchInstitutions();
  }, [isAuthenticated, user, token]);

  // Reload institutions when organization selection changes
  useEffect(() => {
    if (user?.user_role === 'global_admin') {
      fetchInstitutions();
      // Clear institution selection when org changes
      setSelectedInstitutionId(null);
    }
  }, [selectedOrganizationId]);

  // Initialize selections based on user role
  useEffect(() => {
    if (!user) return;
    
    // For non-global admins, auto-select their organization
    if (user.user_role !== 'global_admin' && user.organization_id) {
      setSelectedOrganizationId(user.organization_id);
    }
    
    // Try to restore previous selections from localStorage
    const savedOrgId = localStorage.getItem('selected_organization_id');
    const savedInstId = localStorage.getItem('selected_institution_id');
    
    if (user.user_role === 'global_admin' && savedOrgId) {
      setSelectedOrganizationId(parseInt(savedOrgId));
    }
    
    if (savedInstId && (user.user_role === 'global_admin' || user.user_role === 'org_admin')) {
      setSelectedInstitutionId(parseInt(savedInstId));
    }
  }, [user]);

  // Save selections to localStorage
  useEffect(() => {
    if (selectedOrganizationId) {
      localStorage.setItem('selected_organization_id', selectedOrganizationId.toString());
    } else {
      localStorage.removeItem('selected_organization_id');
    }
  }, [selectedOrganizationId]);

  useEffect(() => {
    if (selectedInstitutionId) {
      localStorage.setItem('selected_institution_id', selectedInstitutionId.toString());
    } else {
      localStorage.removeItem('selected_institution_id');
    }
  }, [selectedInstitutionId]);

  const value: OrgInstitutionContextType = {
    selectedOrganizationId,
    selectedInstitutionId,
    organizations,
    institutions,
    setSelectedOrganizationId,
    setSelectedInstitutionId,
    isLoading,
    refreshInstitutions: fetchInstitutions
  };

  return (
    <OrgInstitutionContext.Provider value={value}>
      {children}
    </OrgInstitutionContext.Provider>
  );
};

